import React, { useContext, useMemo } from 'react';
import styled from 'styled-components/macro';
import { isEmpty } from 'lodash';
import { Focusable } from 'components/navigation';
import scaleSize from 'utils/scale-size';
import { getImageUrlWithResizer } from 'utils/image';
import { ChannelType, LiveEventType } from 'store/pages/loaders/loader-types';
import { EPGContext } from './EPGContext';
import ProgramItem, {
  StyledProgramMeta,
  StyledProgramTitle,
  StyledProgramWrapper,
} from './ProgramItem';

const StyledCoverWrapper = styled.div`
  display: inline-block;
  text-align: center;
  width: ${scaleSize(250)}px;
  height: ${scaleSize(120)}px;
  box-sizing: border-box;
  border: 2px solid rgba(255, 255, 255, 0.05);
  border-radius: 5px;
  background: #26262c;
`;

interface IStyledChannelImage {
  url: string;
}

const StyledChannelImage = styled.div.attrs<IStyledChannelImage>((props) => {
  return {
    style: {
      backgroundImage: `url(${props.url})`,
    },
  };
})<IStyledChannelImage>`
  width: ${scaleSize(120)}px;
  height: ${scaleSize(120)}px;
  background-size: ${scaleSize(120)}px;
  position: relative;
  display: inline-block;
  background-repeat: no-repeat;
`;

const StyledProgramsBlock = styled.div`
  width: ${scaleSize(1600)}px;
  height: ${scaleSize(120)}px;
  box-sizing: border-box;
  position: relative;
  vertical-align: top;
  margin-left: ${scaleSize(10)}px;
  display: inline-block;
`;

const ListItemWrapper = styled(Focusable)`
  width: ${scaleSize(1780)}px;
  height: ${scaleSize(120)}px;
  display: inline-block;
  white-space: nowrap;
  text-align: left;
  margin-right: ${scaleSize(30)}px;
  margin-top: ${scaleSize(5)}px;
  margin-bottom: ${scaleSize(5)}px;
  box-sizing: border-box;
  vertical-align: top;
  position: relative;

  &:focus {
    height: ${scaleSize(180)}px;

    ${StyledCoverWrapper} {
      height: ${scaleSize(180)}px;
      background-color: #18181b;
      border: none;
    }
    ${StyledChannelImage} {
      height: ${scaleSize(180)}px;
      background-size: ${scaleSize(180)}px;
      background-position: center center;
    }

    ${StyledProgramsBlock} {
      height: ${scaleSize(180)}px;
    }

    ${StyledProgramTitle} {
      font-size: ${scaleSize(34)}px;
      line-height: ${scaleSize(40)}px;
    }

    ${StyledProgramMeta} {
      display: block;
    }

    ${StyledProgramWrapper} {
      display: none;
    }

    ${StyledProgramWrapper}.no-content {
      display: inline-block;
      height: ${scaleSize(180)}px;
      width: ${scaleSize(940)}px !important;
      border: ${scaleSize(5)}px solid ${({ theme }) => theme.activeColor};
      border-radius: ${scaleSize(5)}px;

      ${StyledProgramTitle} {
        line-height: ${scaleSize(80)}px;
      }
    }

    ${StyledProgramWrapper}.live {
      display: inline-block;
      height: ${scaleSize(180)}px;
      width: ${scaleSize(940)}px !important;
      border: ${scaleSize(5)}px solid ${({ theme }) => theme.activeColor};
      border-radius:${scaleSize(5)}px;
    }

    // next to live program
    ${StyledProgramWrapper}.live + ${StyledProgramWrapper} {
      display: inline-block;
      height: ${scaleSize(180)}px;
      width: ${scaleSize(940)}px !important;
      border: ${scaleSize(5)}px solid transparent;
    }
  }
`;

interface IListItem {
  id: string;
  index: number;
  item: ChannelType & { epg: LiveEventType[] };
  isActive?: boolean;
  onFocus: (index: number) => void;
  onEnter: (payload: number) => void;
  className?: string;
}

const ChannelListItem: React.FC<IListItem> = (props) => {
  const { sheetStartTimeMs, sheetEndTimeMs, currentTimeMs, isLoading } = useContext(EPGContext);
  const { item, id, index } = props;
  const { onFocus, onEnter, className } = props;

  const imgUrl = getImageUrlWithResizer(item, { width: 100, height: 100 });

  // reduce of past and future programs outside timesheet
  const filtered = useMemo(() => {
    return item?.epg?.filter((program) => {
      const start = new Date(program.start_time).getTime();
      const end = new Date(program.end_time).getTime();
      return end >= sheetStartTimeMs && start <= sheetEndTimeMs;
    });
  }, [item?.epg, sheetEndTimeMs, sheetStartTimeMs]);

  return (
    <ListItemWrapper
      key={id}
      id={id}
      className={className}
      onFocus={onFocus}
      onEnter={onEnter}
      payload={index}
      index={index}
    >
      <StyledCoverWrapper>
        <StyledChannelImage url={imgUrl}></StyledChannelImage>
      </StyledCoverWrapper>
      <StyledProgramsBlock>
        {!isLoading &&
          filtered?.map((program) => {
            return (
              <ProgramItem
                key={program.id}
                item={program}
                channelName={item.name}
                currentTime={currentTimeMs}
                timeSheetStartTime={sheetStartTimeMs}
              />
            );
          })}
        {isEmpty(filtered) && !isLoading && <ProgramItem.NO_CONTENT />}
      </StyledProgramsBlock>
    </ListItemWrapper>
  );
};

export default ChannelListItem;
